import React, { useState, useEffect, useContext } from "react";
import { useParams, useLocation, useHistory, Link } from "react-router-dom";
import { NotificationAlertContext } from "contexts/notificationAlertContext";
import { API, graphqlOperation } from "aws-amplify";
import ReactTable from "components/ReactTable/ReactTableWithDynamicPagination.js";
import { getMirthResendTaskDetails } from "graphql/queries.js";

// react-bootstrap components
import {
    Button,
    Container,
    Row,
    Col,
    Spinner,
    Card,
} from "react-bootstrap";
import Select from "react-select";
import {
  MultiValueWithCustomTooltip,
  customEllipsisMultiSelectStyles,
} from "components/Custom/CustomMultiSelect.js";

function ResendTaskDetails() {
    const { taskId, clientEnvironmentId, dateRollup } = useParams();
    const location = useLocation();
    const history = useHistory();
    const notify = useContext(NotificationAlertContext);
    // Get parameters from navigation state or URL params
    const stateProps = location.state || {};
    const searchParams = new URLSearchParams(location.search);
    const [taskData, setTaskData] = useState({
        task_id: "",
        environment_name: "",
        client_name: "",
        channel_name: "",
        channel_id: "",
        sent_total: 0,
        error_total: 0,
        total_count: 0,
        messages: []
    });
    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);

    // Extract required parameters with fallback priority
    const clientEnvId = stateProps.clientEnvironmentId || searchParams.get('clientEnvironmentId') || clientEnvironmentId || "";
    const cloudTaskId = stateProps.cloudTaskId || searchParams.get('cloudTaskId') || taskId || "";
    const taskDateRollup = stateProps.dateRollup || searchParams.get('dateRollup') || dateRollup || "";

    // Pagination states
    const [currentLimit, setCurrentLimit] = useState(50);
    const [currentOffset, setCurrentOffset] = useState(0);
    const [ascOrDescValue, setAscOrDescValue] = useState("ASC");
    const [sortValue, setSortValue] = useState("Message Id");
    const [isFirstQuery, setIsFirstQuery] = useState(true);

    // Filter state
    const [statusFilter, setStatusFilter] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    // Status filter options
    const statusFilterOptions = [
        { value: "SUCCESS", label: "Success" },
        { value: "ERROR", label: "Error" },
    ];

    const numberOfRowsData = [25, 50, 100];

    const columnMapping = {
        "Message Id": "MSG_ID",
        "Status": "MSG_STATUS",
        "Type": "message_type",
        "Date Received": "DATE_RECEIVED",
        "Date Completed": "DATE_COMPLETED",
    };

    const setStateForPagnination = async (key, value) => {
        if (key === "currentLimit") {
            setCurrentLimit(value);
        } else if (key === "offset") {
            setCurrentOffset(value);
        } else if (key === "ascOrDesc") {
            setAscOrDescValue(value);
        } else {
            setSortValue(value);
        }
    };


    // Handle filter change
    const handleStatusFilterChange = (selectedOptions) => {
        setStatusFilter(selectedOptions || []);
    };

    // Handle search button click
    const handleSearch = async () => {
        setCurrentOffset(0);
        setIsFirstQuery(true); // Reset to get accurate total for new filter
        
        // Refetch data with new filter
        const filterObj = {
            offSet: 0,
            limit: currentLimit,
            sortFilter: columnMapping[sortValue],
            sortType: ascOrDescValue,
        };
        await fetchResendTaskDetails(filterObj, statusFilter);
    };

    // Fetch task details using GraphQL
    const fetchResendTaskDetails = async (variables = {}, newFilter = [], isCSVDownload = false) => {
        try {
            setLoading(true);

            // Prepare status filter for API - handle both array and string inputs
            let msgStatuses = [];
            if (Array.isArray(newFilter)) {
                msgStatuses = newFilter.map(filter => filter.value || filter);
            } else if (newFilter && newFilter !== "All" && newFilter !== "") {
                msgStatuses = [newFilter];
            }

            const response = await API.graphql(
                graphqlOperation(getMirthResendTaskDetails, {
                    clientEnvironmentId: clientEnvId,
                    cloudTaskId: cloudTaskId,
                    dateRollup: taskDateRollup,
                    limit: variables.limit || currentLimit,
                    offset: variables.offSet || currentOffset,
                    accurateTotal: isFirstQuery,
                    msgResendStatuses: msgStatuses,
                    sortFilter: variables.sortFilter || columnMapping[sortValue],
                    sortType: variables.sortType || ascOrDescValue
                })
            );

            const result = response.data.getMirthResendTaskDetails;

            if (result.body) {
                setTaskData(result.body);
                // Since filtering is now done server-side, use all messages
                setFilteredData(result.body.messages || []);

                // Set isFirstQuery to false after first successful API call
                if (isFirstQuery) {
                    setTotalCount(result.body.total_count || 0);
                    setIsFirstQuery(false);
                }

                if (!isCSVDownload) {
                    notify("success", "Task details loaded successfully");
                }
                return true;
            } else {
                throw new Error(result.error || result.message || "Failed to fetch task details");
            }
        } catch (error) {
            console.error("Error fetching task details:", error);
            history.push("/admin/mirth-tasks");
            notify("danger", `Failed to load task details: ${error.message}`);
            return false;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        async function fetchData() {
            if (clientEnvId && cloudTaskId && taskDateRollup) {
                const filterObj = {
                    offSet: 0,
                    limit: currentLimit,
                    sortFilter: columnMapping[sortValue],
                    sortType: ascOrDescValue,
                };
                setCurrentOffset(0);
                const isSuccess = await fetchResendTaskDetails(filterObj, []);
                if (!isSuccess) {
                    notify("danger");
                }
            } else {
                notify("danger", "Missing required parameters for task details");
                history.push("/admin/mirth-tasks");
            }
        }
        fetchData();
    }, [clientEnvId, cloudTaskId, taskDateRollup]);

    if (loading) return <Spinner animation="grow" />;

    return (
        <>
            <Container fluid>
                <Row>
                    <Col>
                        <h2>Mirth Task - Resend Details</h2>
                    </Col>
                </Row>
                {taskData.task_id && (
                    <Row>
                        <Col md="12">
                            <h5>Task Id: {taskData.task_id}</h5>
                            <h5>Client Name: {taskData.client_name}</h5>
                            <h5>Environment Name: {taskData.environment_name}</h5>
                            <h5>Channel Name: {taskData.channel_name}</h5>
                            <h5>Sent: {taskData.sent_total}</h5>
                            <h5>Error: {taskData.error_total}</h5>
                        </Col>
                    </Row>
                )}

                <Row>
                    <Col md="12">
                        <Card>
                            <Card.Body>
                                <Row className="align-items-end">
                                    <Col md="4" sm="12">
                                        <label>Message Status Filter</label>
                                        <Select
                                            className="react-select primary"
                                            classNamePrefix="react-select"
                                            value={statusFilter}
                                            onChange={handleStatusFilterChange}
                                            options={statusFilterOptions}
                                            placeholder="Select Status(es)"
                                            isMulti
                                            styles={customEllipsisMultiSelectStyles}
                                            components={{ MultiValue: MultiValueWithCustomTooltip }}
                                            closeMenuOnSelect={false}
                                        />
                                    </Col>
                                    <Col md="2" sm="12" className="d-flex align-items-end">
                                        <Button
                                            className="btn-fill"
                                            color="primary"
                                            type="button"
                                            onClick={handleSearch}
                                        >
                                            Search
                                        </Button>
                                    </Col>
                                    <Col md="2" sm="12" className="d-flex align-items-end">
                                        <Button
                                            className="btn-fill"
                                            color="secondary"
                                            type="button"
                                            onClick={async () => {
                                                setStatusFilter([]);
                                                setIsFirstQuery(true); // Reset to get accurate total for cleared filter
                                                const filterObj = {
                                                    offSet: 0,
                                                    limit: currentLimit,
                                                    sortFilter: columnMapping[sortValue],
                                                    sortType: ascOrDescValue,
                                                };
                                                await fetchResendTaskDetails(filterObj, []);
                                            }}
                                        >
                                            Clear Filter
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    <Col md="12">
                        {taskData && (
                            <ReactTable
                                data={filteredData || []}
                                totalCount={totalCount}
                                functionCallBack={fetchResendTaskDetails}
                                setStateForPagnination={setStateForPagnination}
                                currentLimit={currentLimit}
                                currentOffset={currentOffset}
                                ascOrDescValue={ascOrDescValue}
                                sortValue={sortValue}
                                numberOfRowsData={numberOfRowsData}
                                columnMapping={columnMapping}
                                tableName="Task Details"
                                CSVHeaders={[
                                    { key: "message_id", label: "Message Id" },
                                    { key: "message_status", label: "Status" },
                                    { key: "message_type", label: "Type" },
                                    { key: "message_date_received", label: "Date Received" },
                                    { key: "message_date_completed", label: "Date Completed" },
                                ]}
                                CSVFileName="Task_Details_Export"
                                notify={notify}
                                columns={[
                                    {
                                        Header: "Message Id",
                                        accessor: "message_id",
                                        filterable: false,
                                    },
                                    {
                                        Header: "Status",
                                        accessor: "message_status",
                                        filterable: false,
                                    },
                                    {
                                        Header: "Type",
                                        accessor: "message_type",
                                        filterable: false,
                                    },
                                    {
                                        Header: "Details",
                                        Cell: (cell) => {
                                            const row = cell.row.original;
                                            return (
                                                <Link
                                                    to={{
                                                        pathname: `/admin/mirth-tasks/${cloudTaskId}/task-details/${row.message_id}/message-details`,
                                                        state: {
                                                            clientEnvironmentId: String(clientEnvId || ''),
                                                            cloudTaskId: String(cloudTaskId || ''),
                                                            dateRollup: String(taskDateRollup || ''),
                                                            channelId: String(taskData.channel_id || ''),
                                                            messageId: Number(row.message_id) || 0,
                                                            statusFilter: String(statusFilter || ''),
                                                            clientId: String(stateProps.clientId || ''),
                                                            clientName: String(stateProps.clientName || ''),
                                                            selectedEnvironments: stateProps.selectedEnvironments || [],
                                                            selectedStatuses: stateProps.selectedStatuses || [],
                                                            selectedOutcomes: stateProps.selectedOutcomes || [],
                                                            startDate: stateProps.startDate ? String(stateProps.startDate) : '',
                                                            endDate: stateProps.endDate ? String(stateProps.endDate) : ''
                                                        },
                                                        search: `?clientEnvironmentId=${clientEnvId}&cloudTaskId=${cloudTaskId}&dateRollup=${taskDateRollup}&channelId=${taskData.channel_id}&messageId=${row.message_id}`,
                                                    }}
                                                >
                                                    View Message Details
                                                </Link>
                                            );
                                        },
                                        filterable: false,
                                        sortable: false,
                                    },
                                    {
                                        Header: "Date Received",
                                        Cell: (cell) => {
                                            const row = cell.row.original;
                                            return new Date(row.message_date_received).toLocaleString();
                                        },
                                        filterable: false,
                                        showTooltip: true,
                                    },
                                    {
                                        Header: "Date Completed",
                                        Cell: (cell) => {
                                            const row = cell.row.original;
                                            return new Date(row.message_date_completed).toLocaleString();
                                        },
                                        filterable: false,
                                        showTooltip: true,
                                    },
                                ]}
                                hidePagination={false}
                            />
                        )}
                    </Col>
                </Row>
            </Container>
        </>
    );
}

export default ResendTaskDetails;
